import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ArticleSearchHero } from 'components/ArticleSearchHero';
import { BannerAds } from 'components/BannerAds';
import { Box } from 'components/Box';
import { PageHeader } from 'components/Page/Header/PageHeader';
import { PageFooter } from 'components/Page/PageFooter';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { SearchHero } from 'components/Search/SearchHero';
import { NoAmp } from 'containers/NoAmp';
import { BaseLayout } from 'layouts/Base';
import { CmsApiHeroSearch } from 'modules/cms/api/types/CmsApiHeroSearch';
import { CmsApiLayout } from 'modules/cms/api/types/CmsApiLayout';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { MainStoreState } from 'modules/main/store';
import { setWithSearchHero } from 'modules/main/store/ducks/layout';
import { colors } from 'theme';
import { AmpContext } from 'utils/AmpContext';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { LanguageLocale } from 'utils/constants';
import { deleteCookie, getListCookie } from 'utils/cookies';
import { isDefined } from 'utils/functional';
import { useElementReachedEffect } from 'utils/screen';

import { LayoutChildrenBox } from './Layout.styled';
import { LayoutContentToolbar } from './LayoutContentToolbar';
import { LayoutSearchToolbar } from './LayoutSearchToolbar';

const PageHeaderContainer = styled.div`
  width: 100%;
  left: 0;
  top: 0;
  position: relative;
  z-index: 103;
  box-shadow: none;
  pointer-events: none;
`;

type Props = {
  noAds?: boolean;
  buttonBar?: ReactNode;
  onHomePage?: boolean;
  onCms?: boolean;
  onStaffDashboard?: boolean;
  localeUrl?: Record<LanguageLocale, string>;
  navSection?: 'MAIN' | 'STAFF';
  defaultWithSearchHero?: boolean;
  layout?: CmsApiLayout;
  children?: ReactNode;
  searchMode?: boolean;
  minimalNav?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setUserInitiatedSearch?: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  onSearch?: () => void;
  postSearchToolbar?: ReactNode;
  darkMode?: boolean;
  showListingCounts?: boolean;
  hideFooterDivider?: boolean;
  stickySidebar?: ReactNode;
  toolbar?: ReactNode;
  hero?: CmsApiHeroSearch | null | undefined;
  content?: CmsApiPage;
  defaultSearchType?: DefaultSearchType;
};

export function Layout({
  noAds,
  buttonBar,
  content,
  localeUrl,
  navSection = 'MAIN',
  defaultWithSearchHero,
  layout,
  children,
  setUserInitiatedSearch,
  onSearch,
  minimalNav,
  postSearchToolbar,
  darkMode,
  stickySidebar,
  showListingCounts,
  hideFooterDivider,
  toolbar,
  searchMode,
  hero,
  defaultSearchType,
  ...props
}: Props) {
  const { withSearchHero } = useSelector((state: MainStoreState) => ({
    withSearchHero: state.layout.withSearchHero,
  }));

  const dispatch = useDispatch();

  const [searchOpen, setSearchOpen] = useState(false);
  const [showStickyMenu, setShowStickyMenu] = useState(false);

  const pageUrl = useMemo(
    () => ({ en: localeUrl?.en, es: localeUrl?.es, pt: localeUrl?.pt }),
    [localeUrl]
  );

  const heroRef = useRef(null);
  const requestIsAmp = useContext(AmpContext);
  const queuedTrackers = getListCookie('trackEvents');

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queuedTrackers.forEach((e: any) => trackEvent(e.type));
    deleteCookie('trackEvents');
  }, [queuedTrackers]);

  useEffect(() => {
    if (
      isDefined(defaultWithSearchHero) &&
      defaultWithSearchHero !== withSearchHero
    ) {
      dispatch(setWithSearchHero(defaultWithSearchHero));
    }
  }, [defaultWithSearchHero, withSearchHero, dispatch]);

  const toggleSearch = (open = !searchOpen) => {
    setSearchOpen(open);
  };

  const renderWithSearchHero = isDefined(defaultWithSearchHero)
    ? defaultWithSearchHero
    : withSearchHero;
  const hideHeroSearch = isDefined(searchMode) && searchMode && !requestIsAmp;

  const bottomOfPageRef = useRef<HTMLDivElement>(null);
  useElementReachedEffect({
    ref: bottomOfPageRef,
    onReach: useCallback(() => {
      // Prevent event from triggering when going from one page to another
      // and the contents of the page is loading
      if (document.documentElement.scrollTop === 0) return false;

      trackEvent('Scroll: Bottom of Page', {
        reached_through: 'Scrolling',
      });

      return true;
    }, []),
  });
  return (
    <BaseLayout
      buttonBar={buttonBar}
      // @ts-expect-error TS(2322): Type '(number | null)[]' is not assignable to type... Remove this comment to see the full error message
      stickyHeaderHeight={
        isDefined(postSearchToolbar)
          ? [135, 135, 135, 75]
          : [60, 60, 60, searchMode || !renderWithSearchHero ? 83 : null]
      }
      header={
        <>
          {!noAds ? <BannerAds /> : null}

          <PageHeaderContainer>
            <Box>
              <PrintHidden>
                <PageHeader
                  minimalNav={minimalNav}
                  pageUrl={pageUrl}
                  navSection={navSection}
                  requestIsAmp={requestIsAmp}
                  withSearchHero={renderWithSearchHero}
                  toggleSearch={toggleSearch}
                />
              </PrintHidden>
            </Box>
          </PageHeaderContainer>

          <LayoutSearchToolbar
            hasPostSearchToolbar={Boolean(postSearchToolbar)}
            showStickyMenu={showStickyMenu}
            renderWithSearchHero={renderWithSearchHero}
            searchMode={Boolean(searchMode)}
            defaultSearchType={defaultSearchType}
            navSection={navSection}
            searchOpen={searchOpen}
            toggleSearch={toggleSearch}
            onSearch={onSearch}
          />

          <LayoutContentToolbar
            content={content}
            postSearchToolbar={postSearchToolbar}
          />
        </>
      }
    >
      {toolbar}

      {hero && (
        <Box
          position="relative"
          height={hideHeroSearch ? 0 : 'auto'}
          overflow={hideHeroSearch && 'hidden'}
          // @ts-expect-error TS(2322): Type 'string | false | undefined' is not assignabl... Remove this comment to see the full error message
          bg={darkMode && colors.backgroundGrey}
        >
          <div ref={heroRef}>
            {defaultSearchType === 'CMS_PAGE' ? (
              <ArticleSearchHero
                title={hero.text}
                image={hero.image}
                imageMobile={hero.imageMobile}
                // @ts-expect-error TS(2322): Type 'CmsApiPage | undefined' is not assignable to... Remove this comment to see the full error message
                content={content}
              />
            ) : (
              <SearchHero
                setShowStickyMenu={setShowStickyMenu}
                title={hero.text}
                // @ts-expect-error TS(2322): Type 'FilestackImageData | null | undefined' is no... Remove this comment to see the full error message
                image={hero.image}
                // @ts-expect-error TS(2322): Type 'FilestackImageData | null | undefined' is no... Remove this comment to see the full error message
                imageMobile={hero.imageMobile}
                searchMode={hideHeroSearch}
                navSection={navSection}
                // @ts-expect-error TS(2322): Type '((...args: any[]) => any) | undefined' is no... Remove this comment to see the full error message
                onSearch={onSearch}
                defaultSearchType={defaultSearchType}
                showListingCounts={showListingCounts}
              />
            )}
          </div>
        </Box>
      )}

      <LayoutChildrenBox
        position="relative"
        flex="1 1 auto"
        bg={darkMode && colors.backgroundGrey}
        pb={[36, null, null, 54]}
        mb={darkMode && '-1px'}
        borderBottom={darkMode && `1px solid ${colors.selectionGrey}`}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {stickySidebar ? <NoAmp>{stickySidebar}</NoAmp> : null}
        {children}
      </LayoutChildrenBox>
      <PrintHidden>
        <div ref={bottomOfPageRef} />
        <PageFooter url={pageUrl} hideDivider={hideFooterDivider} />
      </PrintHidden>
    </BaseLayout>
  );
}
